import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { AnalysisDecision, AnalysisRedFlag } from "@/types/analysisRun";

import CollapsibleSection from "./CollapsibleSection";

const SEVERITY_STYLES: Record<AnalysisRedFlag["severity"], string> = {
  high: "bg-red-100 text-red-800",
  medium: "bg-amber-100 text-amber-800",
  low: "bg-muted text-muted-foreground",
};

const RULE_STATUS_STYLES: Record<string, string> = {
  pass: "text-emerald-700",
  review: "text-amber-700",
  decline: "text-red-700",
};

function fmtMoney(value: number): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

interface Props {
  decision: AnalysisDecision;
  redFlags: AnalysisRedFlag[];
  analystNotes?: string;
}

/** Production decision from the deterministic rule layer over AI analysis outputs. */
const DecisionPanel: React.FC<Props> = ({ decision, redFlags, analystNotes }) => {
  const [rulesOpen, setRulesOpen] = useState(false);
  const approved = decision.status === "approved";

  // Collapsed by default: the verdict and headline numbers sit in the header so
  // nothing an underwriter needs at a glance is hidden behind the toggle.
  const summary = (
    <>
      <Badge className={approved ? "bg-emerald-600" : "bg-red-600"}>
        {approved ? "Approved" : "Declined"}
      </Badge>
      <span className="text-xs text-muted-foreground">
        {decision.computed.current_leverage_pct}% leverage ·{" "}
        {fmtMoney(decision.computed.average_revenue)}/mo avg revenue ·{" "}
        {decision.computed.active_position_count} active position(s)
      </span>
    </>
  );

  return (
    <CollapsibleSection title="Underwriting decision" defaultOpen={false} summary={summary}>
      {decision.offer && (
        <div className="rounded-md border bg-muted/40 p-3 text-sm">
          <span className="font-medium">Offer:</span> {decision.offer.total_display} at{" "}
          {decision.offer.factor_rate} factor, {fmtMoney(decision.offer.daily_payment)}/day over{" "}
          {decision.offer.term_days} days (adds up to {decision.offer.add_up_to_pct}% leverage)
        </div>
      )}

      {decision.decline_reasons.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase text-red-700">Decline reasons</p>
          <ul className="mt-1 list-disc pl-5 text-sm">
            {decision.decline_reasons.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {decision.review_flags.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase text-amber-700">Review flags</p>
          <ul className="mt-1 list-disc pl-5 text-sm">
            {decision.review_flags.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {redFlags.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Red flags</p>
          <div className="mt-1 space-y-1">
            {redFlags.map((f, i) => (
              <div key={`${f.type}-${i}`} className="flex items-start gap-2 text-sm">
                <Badge variant="outline" className={SEVERITY_STYLES[f.severity]}>
                  {f.severity}
                </Badge>
                <span>{f.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {analystNotes && (
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase text-muted-foreground">Analyst notes</p>
          <p className="mt-1 whitespace-pre-wrap text-sm">{analystNotes}</p>
        </div>
      )}

      <button
        type="button"
        className="mt-3 flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setRulesOpen((v) => !v)}
      >
        {rulesOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        Rule breakdown ({decision.rules.length})
      </button>
      {rulesOpen && (
        <table className="mt-2 w-full text-xs">
          <tbody>
            {decision.rules.map((rule, i) => (
              <tr key={`${rule.rule}-${i}`} className="border-b last:border-0">
                <td className="py-1 pr-3 font-mono">{rule.rule}</td>
                <td
                  className={`py-1 pr-3 font-semibold uppercase ${RULE_STATUS_STYLES[rule.status] ?? ""}`}
                >
                  {rule.status}
                </td>
                <td className="py-1 text-muted-foreground">{rule.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </CollapsibleSection>
  );
};

export default DecisionPanel;
